import { useEffect, useMemo, useState } from "react";
import { CaretLeft, CaretRight, FloppyDisk, Eye } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { wibNow } from "@/lib/time";
import { PageHeader } from "@/components/PageHeader";
import { PrintPreviewModal } from "@/components/PrintPreviewModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MON_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const rupiah = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");

export default function AdminMukafaah() {
  const nowW = wibNow();
  const [month, setMonth] = useState(nowW.getMonth() + 1);
  const [year, setYear] = useState(nowW.getFullYear());
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState({ piket_pagi_rate: 15000, piket_sore_rate: 10000 });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const logoSrc = user?.school === "sdqu" ? "/sdqu_logo.png" : "/tk_logo.png";
  const [showPreview, setShowPreview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/mukafaah?month=${month}&year=${year}`);
      setRows(data.rows);
      setRates(data.rates);
    } catch (e) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  const setField = (tid, field, value) => {
    setRows((rs) => rs.map((r) => {
      if (r.teacher_id !== tid) return r;
      const nr = { ...r, [field]: field === "lain_note" ? value : (value === "" ? "" : Number(value)) };
      nr.total = (Number(nr.mukafaah_pokok) || 0) + (Number(nr.tunjangan) || 0) + nr.piket_pagi_amount + nr.piket_sore_amount + (Number(nr.lain) || 0);
      return nr;
    }));
  };

  const saveRow = async (r) => {
    try {
      await api.put(`/admin/mukafaah/${r.teacher_id}?month=${month}&year=${year}`, {
        mukafaah_pokok: Number(r.mukafaah_pokok) || 0,
        tunjangan: Number(r.tunjangan) || 0,
        lain: Number(r.lain) || 0,
        lain_note: r.lain_note || "",
      });
      toast.success("Tersimpan", { duration: 900 });
    } catch (e) {
      toast.error("Gagal menyimpan");
    }
  };

  const saveRates = async (next) => {
    try {
      await api.put(`/admin/mukafaah-rates`, next);
      toast.success("Tarif disimpan", { duration: 900 });
      load();
    } catch (e) {
      toast.error("Gagal menyimpan tarif");
    }
  };

  const totals = useMemo(() => {
    const t = { mukafaah_pokok: 0, tunjangan: 0, piket_pagi_amount: 0, piket_sore_amount: 0, lain: 0, total: 0 };
    rows.forEach((r) => {
      t.mukafaah_pokok += Number(r.mukafaah_pokok) || 0;
      t.tunjangan += Number(r.tunjangan) || 0;
      t.piket_pagi_amount += r.piket_pagi_amount;
      t.piket_sore_amount += r.piket_sore_amount;
      t.lain += Number(r.lain) || 0;
      t.total += Number(r.total) || 0;
    });
    return t;
  }, [rows]);

  const exportXls = async () => {
    try {
      const res = await api.get(`/admin/export/mukafaah.xlsx?month=${month}&year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Mukafaah_Guru_${MON_FULL[month - 1]}_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("File Excel diunduh");
    } catch (e) {
      toast.error("Gagal export");
    }
  };

  const inputCls = "w-full px-2 py-1 rounded-lg border border-stone-200 text-right text-xs focus:outline-none focus:border-teal-500";

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="mukafaah-page">
      <PageHeader title="Mukafaah Guru" />
      <div className="px-4 pt-4">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="muk-prev-month" onClick={prevMonth} className="w-10 h-10 rounded-full bg-white border border-stone-200 flex items-center justify-center active:scale-95 transition-transform"><CaretLeft size={18} /></button>
          <h2 className="font-heading font-bold text-stone-800 text-lg" data-testid="muk-period">{MON_FULL[month - 1]} {year}</h2>
          <button data-testid="muk-next-month" onClick={nextMonth} className="w-10 h-10 rounded-full bg-white border border-stone-200 flex items-center justify-center active:scale-95 transition-transform"><CaretRight size={18} /></button>
        </div>

        {loading ? (
          <p className="text-center text-stone-400 py-10">Memuat…</p>
        ) : (
          <>
            <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-3 mb-4 overflow-x-auto no-scrollbar" data-testid="muk-card">
              <table className="border-collapse text-xs" data-testid="muk-table">
                <thead>
                  <tr className="bg-teal-800 text-white">
                    <th className="px-2 py-2 text-center font-bold">No</th>
                    <th className="px-2 py-2 text-left font-bold min-w-[120px]">Nama Guru</th>
                    <th className="px-2 py-2 text-center font-bold min-w-[120px]">Mukafaah Pokok</th>
                    <th className="px-2 py-2 text-center font-bold min-w-[110px]">Tunjangan</th>
                    <th className="px-2 py-2 text-center font-bold min-w-[110px]">Piket Pagi</th>
                    <th className="px-2 py-2 text-center font-bold min-w-[110px]">Piket Sore</th>
                    <th className="px-2 py-2 text-center font-bold min-w-[150px]">Lain (+/-)</th>
                    <th className="px-2 py-2 text-center font-bold min-w-[110px]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.teacher_id} data-testid={`muk-row-${r.teacher_id}`} className={i % 2 ? "bg-white" : "bg-teal-50/40"}>
                      <td className="px-2 py-2 text-center text-stone-500 border-t border-teal-100">{i + 1}</td>
                      <td className="px-2 py-2 border-t border-teal-100">
                        <p className="font-semibold text-stone-800 leading-tight">{r.name}</p>
                        <p className="text-[10px] text-stone-400">{r.kelas || "—"}</p>
                      </td>
                      <td className="px-1 py-1 border-t border-teal-100">
                        <input data-testid={`muk-pokok-${r.teacher_id}`} type="number" value={r.mukafaah_pokok} onChange={(e) => setField(r.teacher_id, "mukafaah_pokok", e.target.value)} onBlur={() => saveRow(r)} className={inputCls} />
                      </td>
                      <td className="px-1 py-1 border-t border-teal-100">
                        <input data-testid={`muk-tunjangan-${r.teacher_id}`} type="number" value={r.tunjangan} onChange={(e) => setField(r.teacher_id, "tunjangan", e.target.value)} onBlur={() => saveRow(r)} className={inputCls} />
                      </td>
                      <td className="px-2 py-2 text-right border-t border-teal-100">
                        <p className="font-semibold text-stone-700">{rupiah(r.piket_pagi_amount)}</p>
                        <p className="text-[10px] text-stone-400">{r.piket_pagi_days}× {rupiah(rates.piket_pagi_rate)}</p>
                      </td>
                      <td className="px-2 py-2 text-right border-t border-teal-100">
                        <p className="font-semibold text-stone-700">{rupiah(r.piket_sore_amount)}</p>
                        <p className="text-[10px] text-stone-400">{r.piket_sore_days}× {rupiah(rates.piket_sore_rate)}</p>
                      </td>
                      <td className="px-1 py-1 border-t border-teal-100">
                        <input data-testid={`muk-lain-${r.teacher_id}`} type="number" value={r.lain} onChange={(e) => setField(r.teacher_id, "lain", e.target.value)} onBlur={() => saveRow(r)} className={inputCls + " mb-1"} placeholder="0" />
                        <input data-testid={`muk-lain-note-${r.teacher_id}`} type="text" value={r.lain_note} onChange={(e) => setField(r.teacher_id, "lain_note", e.target.value)} onBlur={() => saveRow(r)} className="w-full px-2 py-1 rounded-lg border border-stone-200 text-xs focus:outline-none focus:border-teal-500" placeholder="keterangan" />
                      </td>
                      <td className="px-2 py-2 text-right font-bold text-teal-700 border-t border-teal-100">{rupiah(r.total)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={8} className="text-center text-stone-400 py-6">Belum ada guru.</td></tr>}
                </tbody>
                <tfoot>
                  <tr className="bg-teal-100 font-bold text-teal-900" data-testid="muk-totals">
                    <td className="px-2 py-2 text-center" colSpan={2}>TOTAL</td>
                    <td className="px-2 py-2 text-right">{rupiah(totals.mukafaah_pokok)}</td>
                    <td className="px-2 py-2 text-right">{rupiah(totals.tunjangan)}</td>
                    <td className="px-2 py-2 text-right">{rupiah(totals.piket_pagi_amount)}</td>
                    <td className="px-2 py-2 text-right">{rupiah(totals.piket_sore_amount)}</td>
                    <td className="px-2 py-2 text-right">{rupiah(totals.lain)}</td>
                    <td className="px-2 py-2 text-right">{rupiah(totals.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200/60 p-4 mb-4 text-xs text-stone-600 space-y-2" data-testid="muk-rates">
              <p className="font-semibold text-stone-800">Tarif Piket (per hari) — bisa diubah</p>
              <div className="flex items-center gap-2">
                <span className="w-24">Piket Pagi</span>
                <input data-testid="muk-rate-pagi" type="number" defaultValue={rates.piket_pagi_rate} onBlur={(e) => saveRates({ ...rates, piket_pagi_rate: Number(e.target.value) || 0 })} className="flex-1 px-2 py-1 rounded-lg border border-stone-200 text-right" />
              </div>
              <div className="flex items-center gap-2">
                <span className="w-24">Piket Sore</span>
                <input data-testid="muk-rate-sore" type="number" defaultValue={rates.piket_sore_rate} onBlur={(e) => saveRates({ ...rates, piket_sore_rate: Number(e.target.value) || 0 })} className="flex-1 px-2 py-1 rounded-lg border border-stone-200 text-right" />
              </div>
              <p className="text-[11px] text-stone-400">Piket Pagi & Sore dihitung otomatis: jumlah hari piket × tarif. Mukafaah Pokok, Tunjangan, Lain, & tarif tersalin otomatis ke bulan berikutnya dan bisa diubah.</p>
            </div>

            <div className="flex gap-2">
              <button data-testid="muk-preview" onClick={() => setShowPreview(true)} className="flex-1 border-2 border-stone-300 text-stone-700 font-semibold py-3 rounded-full text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform">
                <Eye size={18} weight="bold" /> Preview / PDF
              </button>
              <button data-testid="muk-export" onClick={exportXls} className="flex-1 bg-emerald-600 text-white font-semibold py-3 rounded-full text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform">
                <FloppyDisk size={18} weight="bold" /> Export Excel
              </button>
            </div>

            <PrintPreviewModal open={showPreview} onClose={() => setShowPreview(false)} logoSrc={logoSrc}
              title="Mukafaah Guru" subtitle={`${MON_FULL[month - 1]} ${year}`}
              fileName={`Mukafaah_Guru_${MON_FULL[month - 1]}_${year}.pdf`}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["No", "Nama Guru", "Mukafaah Pokok", "Tunjangan", "Piket Pagi", "Piket Sore", "Lain", "Keterangan", "Total"].map((h, idx) => (
                      <th key={idx} style={{ border: "1px solid #0f766e", padding: "9px 8px", background: "#115E59", color: "#ffffff", fontSize: 13, fontWeight: 700, textAlign: idx === 1 || idx === 7 ? "left" : (idx === 0 ? "center" : "right") }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const td = { border: "1px solid #dddddd", padding: "8px 8px", fontSize: 13, color: "#1c1917", textAlign: "right" };
                    return (
                      <tr key={r.teacher_id}>
                        <td style={{ ...td, textAlign: "center", color: "#78716c" }}>{i + 1}</td>
                        <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{r.name}<span style={{ color: "#a8a29e", fontWeight: 400 }}> · {r.kelas || "—"}</span></td>
                        <td style={td}>{rupiah(r.mukafaah_pokok)}</td>
                        <td style={td}>{rupiah(r.tunjangan)}</td>
                        <td style={td}>{rupiah(r.piket_pagi_amount)}</td>
                        <td style={td}>{rupiah(r.piket_sore_amount)}</td>
                        <td style={td}>{rupiah(r.lain)}</td>
                        <td style={{ ...td, textAlign: "left", color: "#78716c" }}>{r.lain_note || ""}</td>
                        <td style={{ ...td, fontWeight: 700, color: "#0f766e" }}>{rupiah(r.total)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: "#ccfbf1" }}>
                    <td colSpan={2} style={{ border: "1px solid #99f6e4", padding: "9px 8px", fontSize: 13, fontWeight: 800, color: "#115e59", textAlign: "center" }}>TOTAL</td>
                    {[totals.mukafaah_pokok, totals.tunjangan, totals.piket_pagi_amount, totals.piket_sore_amount, totals.lain].map((v, idx) => (
                      <td key={idx} style={{ border: "1px solid #99f6e4", padding: "9px 8px", fontSize: 13, fontWeight: 800, color: "#115e59", textAlign: "right" }}>{rupiah(v)}</td>
                    ))}
                    <td style={{ border: "1px solid #99f6e4" }} />
                    <td style={{ border: "1px solid #99f6e4", padding: "9px 8px", fontSize: 13, fontWeight: 800, color: "#115e59", textAlign: "right" }}>{rupiah(totals.total)}</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: 14, fontSize: 12, color: "#78716c" }}>
                <p style={{ margin: 0 }}>Piket Pagi: {rupiah(rates.piket_pagi_rate)}/hari × jumlah hari piket pagi</p>
                <p style={{ margin: "2px 0 0" }}>Piket Sore: {rupiah(rates.piket_sore_rate)}/hari × jumlah hari piket sore</p>
              </div>
            </PrintPreviewModal>
          </>
        )}
      </div>
    </div>
  );
}
